import { supabase } from "../supabase";
import { pmError } from "./errors";
import { needsStepUp, requestStepUp } from "./stepUp";

// ============ CREDENTIAL ENCRYPTION (server-side, per-credential salt) ============
// Encryption happens in /api/encrypt.js. The master key is a server-only
// Vercel secret (ENCRYPTION_KEY). Each credential row carries its own random
// 16-byte salt (encryption_salt column) so compromise of one plaintext does
// not trivially unlock every other credential in the company.
//
// Call-site contract:
//   encryptCredential(plain, companyId)
//     → { encrypted, iv, salt }  ← callers MUST persist all three
//   decryptCredential(cipher, iv, companyId, salt?)
//     → plaintext string; falls back to the legacy company-scoped salt
//       when a row has no salt column populated yet (pre-M15 rows).
//
// For Teller access-token rows created before M15, pass legacyScheme="teller"
// on the first decrypt — the row is re-written with a fresh salt afterwards
// by the save-enrollment path or the one-shot migration.

async function fetchEncrypt(token, body) {
  return fetch("/api/encrypt", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
    body: JSON.stringify(body),
  });
}

async function callEncryptApi(body) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No active session");
  let resp = await fetchEncrypt(token, body);
  // getSession() hands back the stored token without checking it; on a
  // phone the background refresh often has not run, so an idle app sends
  // an expired one ("Invalid session" on Show login, Sanya 2026-10-14).
  // Refresh only AFTER the server has said 401, once, and retry -- the
  // pattern PayBillModal uses. Refreshing up front is what used to fire
  // SIGNED_OUT mid-save (see below).
  if (resp.status === 401) {
    const { data: r } = await supabase.auth.refreshSession().catch(() => ({ data: {} }));
    const fresh = r?.session?.access_token;
    if (fresh && fresh !== token) resp = await fetchEncrypt(fresh, body);
  }
  // A session without a code (a trusted device) asking to reveal a login:
  // the server says mfa_required; ask for the code once and retry (2026-10-24).
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => ({}));
    if (needsStepUp(resp.status, peek) && await requestStepUp("show a stored login")) {
      const { data: { session: s2 } } = await supabase.auth.getSession();
      if (s2?.access_token) resp = await fetchEncrypt(s2.access_token, body);
    }
  }
  if (!resp.ok) {
    // Surface 401 as "Invalid session" so the caller can map it to
    // its own session-expired UX (e.g. the wizard shows a sign-in
    // modal). Calling supabase.auth.refreshSession() here used to
    // trigger SIGNED_OUT when the refresh token was also stale,
    // silently bouncing the user to the landing page mid-save —
    // pushed that responsibility up one level instead.
    let msg = "encrypt API " + resp.status;
    try { msg = (await resp.json()).error || msg; } catch (_) {}
    throw new Error(msg);
  }
  return resp.json();
}

// Throws on real encrypt failure — callers must catch and abort the save
// rather than silently persisting an empty ciphertext (which earlier
// builds did by returning { encrypted: "", iv: "", salt: "" }). Empty
// plaintext is still a valid no-op and returns the empty triple.
export async function encryptCredential(plaintext, companyId, reuseSalt = null) {
  if (!plaintext) return { encrypted: "", iv: "", salt: reuseSalt || "" };
  // reuseSalt lets two paired values (e.g. username + password) share a
  // per-row salt while each still gets its own IV. Persist the salt once
  // on the row; reuse it on the second encrypt so decrypt with the row's
  // encryption_salt succeeds for both values.
  const body = { action: "encrypt", plaintext, companyId };
  if (reuseSalt) body.salt = reuseSalt;
  try {
    return await callEncryptApi(body);
  } catch (e) {
    pmError("PM-8006", { raw: e, context: "encryptCredential — throwing so caller aborts save" });
    throw new Error("Encryption failed: " + (e.message || e));
  }
}

// Returns null on decrypt failure rather than a sentinel placeholder.
// Previous version returned "••••••" which some callers rendered
// straight into an <input value>, persisting the literal dots on save
// and wiping the real stored credential. Callers that want a display
// placeholder should coalesce: `(await decryptCredential(...)) || "—"`.
export async function decryptCredential(encryptedB64, ivHex, companyId, salt = null, legacyScheme = null, keyFp = null) {
  if (!encryptedB64 || !ivHex) return null;
  try {
    // keyFp, when the row has one, lets the server say "encrypted under a
    // different key -- re-enter this" instead of "decryption failed". The
    // difference matters: one is actionable, the other sends someone
    // looking for corruption that is not there.
    const body = { action: "decrypt", ciphertext: encryptedB64, iv: ivHex, companyId };
    if (salt) body.salt = salt;
    if (keyFp) body.keyFp = keyFp;
    if (legacyScheme) body.legacyScheme = legacyScheme;
    const { plaintext } = await callEncryptApi(body);
    return plaintext || "";
  } catch (e) {
    // Withheld by the admin (the person's card in Team & Roles): say so, rather
    // than show "—" as if nothing were stored.
    // An expired sign-in that a refresh could not renew: say so, once,
    // instead of logging a save failure ("Could not save your changes").
    if (/Invalid session|No active session/i.test(String(e?.message || e))) {
      pmError("PM-1001", { raw: e, context: "decryptCredential — sign-in expired" });
      return null;
    }
    pmError("PM-8006", { raw: e, context: "decryptCredential", silent: !/permission/i.test(String(e?.message || e)) });
    return null;
  }
}

// The saved portal login of a row, decrypted, or null when there is none.
// `prefix` picks a second login on the same row ("mgmt_" / "pay_" on HOA
// rows); salt and key fingerprint are shared by every login on a row.
// A login that is saved but cannot be opened (no permission, other key)
// comes back as { user: null, pass: null }, not null.
// Rows from the old key scheme (no salt) are retried under "teller", the
// same fallback the Utilities page uses.
export async function readSavedLogin(row, companyId, prefix = "") {
  const ue = row?.[prefix + "username_encrypted"], pe = row?.[prefix + "password_encrypted"];
  if (!ue || !pe) return null;
  const iv = row[prefix + "encryption_iv"];
  const ivUser = row[prefix + "encryption_iv_username"] || iv;
  let user = await decryptCredential(ue, ivUser, companyId, row.encryption_salt || null, null, row.credential_key_fp || null);
  let pass = await decryptCredential(pe, iv, companyId, row.encryption_salt || null, null, row.credential_key_fp || null);
  if ((user == null || pass == null) && !row.encryption_salt) {
    user = await decryptCredential(ue, ivUser, companyId, null, "teller");
    pass = await decryptCredential(pe, iv, companyId, null, "teller");
  }
  return { user, pass };
}
